import {
  AnnouncementDismissBehavior,
  AnnouncementSeverity,
  type ActiveAnnouncementDto,
} from "@/features/announcements/model/announcement-types";

export type BannerToneClasses = {
  /** Outer panel — background + text + bottom border per severity. */
  panel: string;
  /** Icon color emphasis. */
  accent: string;
  /** Dismiss icon color. */
  dismiss: string;
};

// Dismiss buttons are deliberately styled with a *hover-visible*
// background chip so the close affordance is obvious. We use the
// foreground colour for the icon (not muted) so it stays legible at
// rest, and add a soft tone-matched hover background so the hit area
// is clear once the user mouses over it.
const TONES: Record<AnnouncementSeverity, BannerToneClasses> = {
  [AnnouncementSeverity.Info]: {
    panel: "bg-muted/40 text-foreground border-b border-border/60",
    accent: "text-foreground/80",
    dismiss: "text-foreground hover:bg-foreground/10",
  },
  [AnnouncementSeverity.Success]: {
    panel:
      "bg-emerald-50 text-emerald-900 border-b border-emerald-200/70 dark:bg-emerald-950/40 dark:text-emerald-100 dark:border-emerald-900/60",
    accent: "text-emerald-700 dark:text-emerald-300",
    dismiss:
      "text-emerald-900 hover:bg-emerald-900/10 dark:text-emerald-100 dark:hover:bg-emerald-100/15",
  },
  [AnnouncementSeverity.Warning]: {
    panel:
      "bg-amber-50 text-amber-900 border-b border-amber-200/70 dark:bg-amber-950/40 dark:text-amber-100 dark:border-amber-900/60",
    accent: "text-amber-700 dark:text-amber-300",
    dismiss:
      "text-amber-900 hover:bg-amber-900/10 dark:text-amber-100 dark:hover:bg-amber-100/15",
  },
  [AnnouncementSeverity.Critical]: {
    panel: "bg-red-600 text-white border-b border-red-700",
    accent: "text-white",
    dismiss: "text-white hover:bg-white/15",
  },
};

export function getBannerToneClasses(severity: AnnouncementSeverity): BannerToneClasses {
  return TONES[severity] ?? TONES[AnnouncementSeverity.Info];
}

/**
 * Defensive last-resort sanitization. The backend (`rich-text-sanitizer.ts`)
 * is the authoritative sanitizer — it strips scripts, event handlers, and
 * `javascript:` hrefs before the DTO ever reaches us. This guard exists so a
 * misconfiguration or future regression can't bypass the contract entirely.
 *
 * We intentionally do not parse / rewrite the HTML here — that's the API's
 * job. We just neutralise the most dangerous patterns at the string level.
 */
export function formatBannerMessage(html: string): string {
  if (!html) return "";

  let out = html;

  // Remove any <script>...</script> blocks, including those split across
  // newlines or with attributes. Non-greedy + case-insensitive + dotall.
  out = out.replace(/<script\b[^>]*>[\s\S]*?<\/script\s*>/gi, "");
  // Remove standalone (unclosed) <script ...> tags too.
  out = out.replace(/<script\b[^>]*\/?>/gi, "");
  // Strip inline event handlers (onclick=, onerror=, …).
  out = out.replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "");
  // Neutralise javascript: hrefs/src on any element.
  out = out.replace(/(href|src)\s*=\s*(["'])\s*javascript:[^"']*\2/gi, '$1="#"');

  return out;
}

export function isCriticalNotDismissible(announcement: ActiveAnnouncementDto): boolean {
  return (
    announcement.severity === AnnouncementSeverity.Critical &&
    announcement.dismissBehavior === AnnouncementDismissBehavior.NotDismissible
  );
}

export function isDismissible(announcement: ActiveAnnouncementDto): boolean {
  return announcement.dismissBehavior !== AnnouncementDismissBehavior.NotDismissible;
}
